// Library vertical L2 — the newsletter membership designation, as a consumable
// surface.
//
// L3 (KG bundle lookup → orchestrator → deterministic compiler) must CONSUME
// this designation rather than re-derive fixture paths and authority contexts,
// which is how a designation ends up "wired but unreachable". Everything here
// is a read of the checked-in designation artifacts:
//
//   * the Blueprint + UX pattern JSON fixtures in ./fixtures/newsletter-membership/,
//     which are drift-locked against the TTL shard
//     packages/cogo-kg-browser/fixtures/newsletter-membership-application-blueprint-v1.ttl
//     by packages/cogo-kg-browser/__tests__/newsletter-membership-application-blueprint-v1.test.ts;
//   * the knowledge-unit admission record
//     packages/cogo-validator/ontology/library-designations.seed.json.
//
// This module holds NO second authority — no re-declared capability contract,
// no re-declared design binding. The capability contract comes from
// @cogo/types-v2 and the Design Ontology binding from the L1 shard resolver.
//
// ─────────────────────────────────────────────────────────────────────────────
// L2 shipped this designation with a KNOWN, deliberate gap: the DESIGN stage
// could not compile, because the bound release `library-baseline-web-v1` did
// not carry the newsletter's design vocabulary (no
// `cogo:composition-recipe/newsletter-membership-v1`, no `ds:ComponentContract`
// for 6 of its semantic roles, no `decision-secondary` `ds:ActionSlotContract`).
// L2 refused to close it by renaming newsletter roles onto the task-shaped ones,
// because that makes a compile pass by misdescribing the semantics.
//
// L3 CLOSED it the intended way — by authoring the missing vocabulary in the
// release ABox (`packages/cogo-kg-browser/fixtures/design-binding-v1.ttl` and
// its parity twin `src/design-ontology-binding/fixtures/library-baseline.release.json`):
// 6 newsletter ComponentContracts, the `decision-secondary` ActionSlot on the
// new secondary-action component, and the newsletter recipe with its
// intro/capture/roster/decision regions. `primary-action` is shared with the
// mobile application rather than duplicated; only its slot's admitted
// capability refs grew.
//
// The closed state is a live fact, not a comment: the tests
// "compiles through the design-bound compiler", "records the closed L3
// design-stage gap as a live fact" and "declares exactly the Design Ontology
// tokens the compiled application uses" in this module's test file re-fail the
// moment any of that vocabulary leaves the release.
// ─────────────────────────────────────────────────────────────────────────────

import applicationBlueprint from "./fixtures/newsletter-membership/newsletter-membership.application-blueprint.json"
import memberContent from "./fixtures/newsletter-membership/newsletter-member-content.ux-pattern.json"
import membershipSignup from "./fixtures/newsletter-membership/newsletter-membership-signup.ux-pattern.json"
import operatorImpactControls from "./fixtures/newsletter-membership/newsletter-operator-impact-controls.ux-pattern.json"
import operatorOverview from "./fixtures/newsletter-membership/newsletter-operator-overview.ux-pattern.json"
import type {
  ApplicationBlueprint,
  ApplicationCapabilityRequirement,
  ApplicationWebMcpMode,
  LibraryAuthorityContext,
  LibraryCompositionInput,
  UxPatternDefinition,
} from "./types"

/**
 * Where the designation lives. L3's KG bundle lookup resolves the Library entry
 * through these refs; nothing else in the repo re-states them.
 */
export const NEWSLETTER_MEMBERSHIP_DESIGNATION = {
  applicationBlueprintId: "newsletter-membership",
  applicationInstanceId: "golden-sku-reference",
  applicationCoreRef: "cogo:application-core/newsletter-membership-v1",
  shardRef:
    "packages/cogo-kg-browser/fixtures/newsletter-membership-application-blueprint-v1.ttl",
  shapesRef: "packages/cogo-kg-browser/fixtures/ux-application-library-shapes-v1.ttl",
  admissionRecordRef:
    "packages/cogo-validator/ontology/library-designations.seed.json",
  admissionRecordId: "ku:library-designation-v1:newsletter-membership",
  /** The published application these structure refs were designated from. */
  sourceApplicationRef:
    "artifact:apps/cogo-starter/public/apps/measure/newsletter-membership-reference/app.json",
  exactNamespace:
    "cogo://application-blueprint/newsletter-membership/golden-sku-reference",
} as const

/**
 * The lineage refs this designation is admitted against. An unlisted ref is a
 * `library-source-forbidden` rejection, so this is a closed list, not a hint.
 */
export const NEWSLETTER_MEMBERSHIP_LIBRARY_AUTHORITY = {
  admittedUxResearchRefs: [],
  approvedCanonicalSourceRefs: [
    "cogo:approved-source/newsletter-member-content-v1",
    "cogo:approved-source/newsletter-membership-signup-v1",
    "cogo:approved-source/newsletter-operator-impact-controls-v1",
    "cogo:approved-source/newsletter-operator-overview-v1",
  ],
  admittedApplicationCoreRefs: [
    NEWSLETTER_MEMBERSHIP_DESIGNATION.applicationCoreRef,
  ],
} satisfies LibraryAuthorityContext

export const NEWSLETTER_MEMBERSHIP_UX_PATTERNS = [
  membershipSignup,
  memberContent,
  operatorOverview,
  operatorImpactControls,
] as unknown as UxPatternDefinition[]

export const NEWSLETTER_MEMBERSHIP_APPLICATION_BLUEPRINT =
  applicationBlueprint as unknown as ApplicationBlueprint

/**
 * Newsletter v1 declares `disabled` statically, so the request-bound
 * requirement carries the same mode and an empty evidence list — the only
 * combination the capability contract admits with zero evidence.
 */
export const NEWSLETTER_MEMBERSHIP_CAPABILITY_REQUEST_HASH =
  `sha256:${"0".repeat(64)}` as const

export function newsletterMembershipCapabilityRequirement(): ApplicationCapabilityRequirement {
  return {
    schemaVersion: "cogo.application-capability-requirement.v1",
    requestedWebMcpMode: applicationBlueprint.runtimeCapabilities.webMcp
      .mode as ApplicationWebMcpMode,
    requestHash: NEWSLETTER_MEMBERSHIP_CAPABILITY_REQUEST_HASH,
    requirementEvidenceRefs: [],
  }
}

/**
 * The deterministic Library composition input for the designated Blueprint.
 *
 * `applicationInstanceId` defaults to the designated reference instance, which
 * is the one the TTL shard's `lib:applicationInstanceId` and exact namespaces
 * are written against; L3 passes its own per-request instance id.
 *
 * The result is a deep clone: a caller mutating its candidate must never reach
 * back into the designation, which is process-wide shared state.
 */
export function newsletterMembershipLibraryCandidate(
  applicationInstanceId: string = NEWSLETTER_MEMBERSHIP_DESIGNATION.applicationInstanceId,
): LibraryCompositionInput {
  return structuredClone({
    applicationInstanceId,
    applicationBlueprint: NEWSLETTER_MEMBERSHIP_APPLICATION_BLUEPRINT,
    uxPatterns: NEWSLETTER_MEMBERSHIP_UX_PATTERNS,
    capabilityRequirement: newsletterMembershipCapabilityRequirement(),
  })
}
